import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, Col, Nav, Row, Button, Spinner, Alert } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { authApi } from "../api/auth";
import { useAuth } from "../contexts/AuthContext"; // ← nouveau

export default function Profile() {
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  // ⚠️ Récupération du contexte pour mettre à jour l'utilisateur partout
  const { user: sessionUser, updateUser } = useAuth();

  // État du profil (initialisé depuis la session pour éviter un flash)
  const [loading, setLoading] = useState(!sessionUser);
  const [user, setUser] = useState(sessionUser || null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});

  // État du mot de passe
  const [passwords, setPasswords] = useState({ current: "", new: "", confirm: "" });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordErrors, setPasswordErrors] = useState({});

  // Chargement des données fraîches depuis l'API (au cas où)
  useEffect(() => {
    if (sessionUser) {
      setUser(sessionUser);
      setLoading(false);
      return;
    }

    authApi.me()
      .then((data) => {
        setUser(data);
        updateUser(data);
      })
      .catch((err) => {
        console.error("Erreur profil:", err);
        setProfileMessage({ type: "danger", text: "Impossible de charger le profil." });
      })
      .finally(() => setLoading(false));
  }, [sessionUser, updateUser]);

  // --- Modification du profil ---
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setUser((u) => ({ ...u, [name]: value }));
    if (profileErrors[name]) setProfileErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileMessage(null);

    const errs = {};
    if (!user.name?.trim()) errs.name = "Le nom est requis.";
    if (!user.email?.trim()) errs.email = "L'email est requis.";
    if (user.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) errs.email = "Email invalide.";
    if (Object.keys(errs).length > 0) {
      setProfileErrors(errs);
      return;
    }

    setSavingProfile(true);
    try {
      const updated = await authApi.updateProfile({
        name: user.name,
        email: user.email,
      });

      const updatedUser = updated.data || updated;
      setUser(updatedUser);

      // ⚠️ Met à jour le contexte global → le Header/Sidebar affichent le nouveau nom
      updateUser(updatedUser);

      setProfileMessage({ type: "success", text: "Profil mis à jour avec succès." });
    } catch (err) {
      const msg = err.response?.data?.error || "Erreur lors de la mise à jour.";
      setProfileMessage({ type: "danger", text: msg });
    } finally {
      setSavingProfile(false);
    }
  };

  // --- Modification du mot de passe ---
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((p) => ({ ...p, [name]: value }));
    if (passwordErrors[name]) setPasswordErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    const errs = {};
    if (!passwords.current) errs.current = "Requis.";
    if (!passwords.new) errs.new = "Requis.";
    if (passwords.new && passwords.new.length < 8) errs.new = "8 caractères minimum.";
    if (passwords.new !== passwords.confirm) errs.confirm = "Ne correspond pas.";
    if (Object.keys(errs).length > 0) {
      setPasswordErrors(errs);
      return;
    }

    setSavingPassword(true);
    try {
      await authApi.updatePassword({
        current_password: passwords.current,
        new_password: passwords.new,
      });
      setPasswordMessage({ type: "success", text: "Mot de passe modifié avec succès." });
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (err) {
      const msg = err.response?.data?.error || "Erreur lors du changement.";
      setPasswordMessage({ type: "danger", text: msg });
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <Header onSkin={setSkin} />
        <div className="main main-app cere-main p-3 p-lg-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
            <Spinner animation="border" style={{ color: "#009e60" }} />
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  const displayName = user?.name || user?.email?.split("@")[0] || "Admin";

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">

        <div className="cere-page-head d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
                <li className="breadcrumb-item active">Mon profil</li>
              </ol>
            </nav>
            <h4 className="cere-page-title mb-0">Mon profil</h4>
            <p className="cere-page-subtitle mb-0">Gérez vos informations personnelles et votre sécurité</p>
          </div>
        </div>

        <Row className="g-4">
          <Col xl="8">
            
            {/* Carte profil */}
            <Card className="cere-card mb-4">
              <Card.Body>
                <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-4">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 100, height: 100, borderRadius: "50%",
                      background: "linear-gradient(135deg, #009e60, #00804d)",
                      color: "#fff", fontSize: "2.5rem", fontWeight: 800,
                    }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-grow-1 text-center text-sm-start">
                    <h4 className="mb-1 fw-bold" style={{ color: "#0b1d1e" }}>{displayName}</h4>
                    <p className="text-success fw-semibold mb-2">
                      <i className="ri-shield-user-line me-1"></i>
                      {user?.role === "admin" ? "Administrateur" : "Éditeur"}
                    </p>
                    <p className="text-muted small mb-0">
                      <i className="ri-mail-line me-1"></i>{user?.email}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Formulaire informations */}
            <Card className="cere-card mb-4">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title">
                  <i className="ri-user-settings-line me-2"></i>
                  Informations personnelles
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {profileMessage && (
                  <Alert variant={profileMessage.type} dismissible onClose={() => setProfileMessage(null)}>
                    {profileMessage.text}
                  </Alert>
                )}
                <form onSubmit={handleProfileSubmit}>
                  <Row className="g-3">
                    <Col md="6">
                      <label className="cere-form-label">Nom complet <span className="cere-required">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={user?.name || ""}
                        onChange={handleProfileChange}
                        className={`cere-form-control ${profileErrors.name ? "is-invalid" : ""}`}
                      />
                      {profileErrors.name && <div className="cere-form-error">{profileErrors.name}</div>}
                    </Col>
                    <Col md="6">
                      <label className="cere-form-label">Adresse email <span className="cere-required">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={user?.email || ""}
                        onChange={handleProfileChange}
                        className={`cere-form-control ${profileErrors.email ? "is-invalid" : ""}`}
                      />
                      {profileErrors.email && <div className="cere-form-error">{profileErrors.email}</div>}
                    </Col>
                    <Col md="6">
                      <label className="cere-form-label">Rôle</label>
                      <input type="text" className="cere-form-control" value={user?.role === "admin" ? "Administrateur" : "Éditeur"} readOnly />
                    </Col>
                    <Col md="6">
                      <label className="cere-form-label">Membre depuis</label>
                      <input
                        type="text"
                        className="cere-form-control"
                        value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString("fr-FR") : "—"}
                        readOnly
                      />
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end mt-4">
                    <Button type="submit" className="cere-btn cere-btn-primary" disabled={savingProfile}>
                      {savingProfile ? (
                        <><Spinner animation="border" size="sm" className="me-2" />Enregistrement…</>
                      ) : (
                        <><i className="ri-save-line me-2"></i>Enregistrer les modifications</>
                      )}
                    </Button>
                  </div>
                </form>
              </Card.Body>
            </Card>

            {/* Formulaire mot de passe */}
            <Card className="cere-card">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title">
                  <i className="ri-lock-line me-2"></i>
                  Sécurité
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {passwordMessage && (
                  <Alert variant={passwordMessage.type} dismissible onClose={() => setPasswordMessage(null)}>
                    {passwordMessage.text}
                  </Alert>
                )}
                <form onSubmit={handlePasswordSubmit}>
                  <Row className="g-3">
                    <Col md="12">
                      <label className="cere-form-label">Mot de passe actuel <span className="cere-required">*</span></label>
                      <input
                        type="password"
                        name="current"
                        value={passwords.current}
                        onChange={handlePasswordChange}
                        className={`cere-form-control ${passwordErrors.current ? "is-invalid" : ""}`}
                        placeholder="••••••••"
                        autoComplete="current-password"
                      />
                      {passwordErrors.current && <div className="cere-form-error">{passwordErrors.current}</div>}
                    </Col>
                    <Col md="6">
                      <label className="cere-form-label">Nouveau mot de passe <span className="cere-required">*</span></label>
                      <input
                        type="password"
                        name="new"
                        value={passwords.new}
                        onChange={handlePasswordChange}
                        className={`cere-form-control ${passwordErrors.new ? "is-invalid" : ""}`}
                        placeholder="8 caractères min."
                        autoComplete="new-password"
                      />
                      {passwordErrors.new && <div className="cere-form-error">{passwordErrors.new}</div>}
                    </Col>
                    <Col md="6">
                      <label className="cere-form-label">Confirmer <span className="cere-required">*</span></label>
                      <input
                        type="password"
                        name="confirm"
                        value={passwords.confirm}
                        onChange={handlePasswordChange}
                        className={`cere-form-control ${passwordErrors.confirm ? "is-invalid" : ""}`}
                        placeholder="••••••••"
                        autoComplete="new-password"
                      />
                      {passwordErrors.confirm && <div className="cere-form-error">{passwordErrors.confirm}</div>}
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end mt-4">
                    <Button type="submit" className="cere-btn cere-btn-primary" disabled={savingPassword}>
                      {savingPassword ? (
                        <><Spinner animation="border" size="sm" className="me-2" />Enregistrement…</>
                      ) : (
                        <><i className="ri-lock-line me-2"></i>Changer le mot de passe</>
                      )}
                    </Button>
                  </div>
                </form>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="cere-card mb-4">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title">
                  <i className="ri-links-line me-2"></i>
                  Actions rapides
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-2">
                <Nav className="flex-column cere-quick-nav">
                  <Nav.Link as={Link} to="/documents/new" className="cere-quick-link">
                    <i className="ri-add-circle-line"></i>Ajouter un document
                  </Nav.Link>
                  <Nav.Link as={Link} to="/documents" className="cere-quick-link">
                    <i className="ri-file-list-3-line"></i>Voir la bibliothèque
                  </Nav.Link>
                  <Nav.Link as={Link} to="/statistics" className="cere-quick-link">
                    <i className="ri-bar-chart-box-line"></i>Voir les statistiques
                  </Nav.Link>
                  <Nav.Link as={Link} to="/categories" className="cere-quick-link">
                    <i className="ri-price-tag-3-line"></i>Gérer les catégories
                  </Nav.Link>
                </Nav>
              </Card.Body>
            </Card>

            <Card className="cere-card">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title">
                  <i className="ri-information-line me-2"></i>
                  À propos du CERE
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <p className="text-muted small mb-3">
                  Le Centre Électronique de Ressources Électorales (CERE) centralise les documents 
                  électoraux et institutionnels du Gabon.
                </p>
                <a href="https://cere-gabon.org" target="_blank" rel="noopener noreferrer" className="cere-btn cere-btn-outline w-100">
                  <i className="ri-external-link-line me-2"></i>Visiter le site public
                </a>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}